import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionHeaderName,
  Loader,
  Notification,
  NotificationType,
} from "@transfr-inc/dashboard-components";

import { useContainer } from "@contexts/container-context";
import { trackAiCurriculumRecommendationFeedback } from "@lib/event-trackers";

import {
  AiCoverageSummary,
  AiRecommendationFeedback,
  AiRecommendationSection,
} from "../components";

import "./ai-recommendations.step.scss";

const groupBySection = (items) => {
  const groups = new Map();
  for (const item of items) {
    const key = item.sectionLabel || "Recommendations";
    if (!groups.has(key)) {
      groups.set(key, {
        label: key,
        order: item.sectionOrder ?? 0,
        items: [],
      });
    }
    groups.get(key).items.push(item);
  }
  return [...groups.values()].sort((a, b) => a.order - b.order);
};

export const AiRecommendationsStep = ({
  orgCode,
  sessionId,
  entryType,
  standardsContext,
  recommendations,
  onRecommendations,
  error,
  onError,
}) => {
  const { aiCurriculumService } = useContainer();
  const [loading, setLoading] = useState(!recommendations);
  const [coverage, setCoverage] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [runVersion, setRunVersion] = useState(null);
  const [removedExpanded, setRemovedExpanded] = useState(false);

  useEffect(() => {
    if (recommendations) return;
    let cancelled = false;
    setLoading(true);

    aiCurriculumService
      .getRecommendations(orgCode, sessionId)
      .then((response) => {
        if (cancelled) return;
        onRecommendations(response?.recommendations ?? []);
        setCoverage(response?.coverage ?? null);
        // Hydrate the thumbs selection from the persisted per-run rating; the
        // run version rides along on the feedback telemetry event.
        setFeedback(response?.feedback ?? null);
        setRunVersion(response?.version ?? null);
      })
      .catch(() => {
        if (!cancelled) onError("We couldn't load your recommendations.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    orgCode,
    sessionId,
    recommendations,
    aiCurriculumService,
    onRecommendations,
    onError,
  ]);

  const active = useMemo(
    () => (recommendations ?? []).filter((r) => !r.removedByUser),
    [recommendations]
  );

  const removed = useMemo(
    () => (recommendations ?? []).filter((r) => r.removedByUser),
    [recommendations]
  );

  const groupedActive = useMemo(() => groupBySection(active), [active]);

  // Chained because each PATCH rewrites the whole stored recommendations
  // artifact — two in flight at once would clobber each other's selection.
  const pendingWrite = useRef(Promise.resolve());

  // Persist one toggle per request so the backend sees the actual flip and
  // emits ai_curriculum_item_removed / _restored (ENG-21163). It derives those
  // events from the diff against the stored artifact, so batching or debouncing
  // would collapse a remove→restore pair back into no change and lose both.
  const applySelection = useCallback(
    (itemId, removedByUser) => {
      const next = recommendations.map((r) =>
        r.id === itemId ? { ...r, removedByUser } : r
      );
      onRecommendations(next);
      pendingWrite.current = pendingWrite.current
        .then(() =>
          aiCurriculumService.updateRecommendationSelection(
            orgCode,
            sessionId,
            { recommendations: next }
          )
        )
        // Non-blocking: the selection lives in component state and is re-sent
        // before Build My Course, so a failed write costs the telemetry event,
        // not the user's choice.
        .catch(() => {});
    },
    [
      orgCode,
      sessionId,
      recommendations,
      onRecommendations,
      aiCurriculumService,
    ]
  );

  const handleRemove = (itemId) => applySelection(itemId, true);

  const handleRestore = (itemId) => applySelection(itemId, false);

  // Optional, non-blocking recommendation-quality feedback (ENG-23158).
  // Optimistically reflect the new selection, then persist it; on failure
  // revert quietly and leave the rest of the screen untouched. The revert
  // only applies while the selection is still the one that failed, so a
  // quick follow-up click is never clobbered by a stale error.
  const handleFeedbackChange = (next) => {
    const previous = feedback;
    setFeedback(next);
    // Telemetry rides alongside persistence (ENG-23190): every select,
    // change, and clear is captured against the run, independent of whether
    // the PUT succeeds.
    trackAiCurriculumRecommendationFeedback(
      next,
      entryType,
      sessionId,
      runVersion
    );
    aiCurriculumService.updateFeedback(orgCode, sessionId, next).catch(() => {
      setFeedback((current) => (current === next ? previous : current));
    });
  };

  if (loading) {
    return (
      <div className="ai-recommendations-step ai-recommendations-step--loading">
        <Loader />
      </div>
    );
  }

  if (active.length === 0 && removed.length === 0) {
    return (
      <div className="ai-recommendations-step ai-recommendations-step--empty">
        <h3>No recommendations yet</h3>
        <p>
          We weren't able to generate recommendations for this combination.
          Please try a different state or profession.
        </p>
      </div>
    );
  }

  return (
    <div className="ai-recommendations-step">
      {error && (
        <Notification type={NotificationType.error}>{error}</Notification>
      )}

      <div className="ai-recommendations-step__banner">
        You have{" "}
        <span className="ai-recommendations-step__count">{active.length}</span>{" "}
        recommendations!
      </div>

      <header className="ai-recommendations-step__header">
        <p className="ai-recommendations-step__instructions">
          Press the X to remove a match. Press Build My Course when you are
          done.
        </p>
        <p className="ai-recommendations-step__instructions">
          <FontAwesomeIcon icon={["fa-regular", "circle-info"]} /> Hover over
          the info icon to view recommendation details.
        </p>
        {standardsContext && (
          <p className="ai-recommendations-step__instructions context">
            Aligned to{" "}
            <strong>
              {standardsContext.sourceName || standardsContext.subject}
            </strong>{" "}
            standards in <strong>{standardsContext.state}</strong>.
          </p>
        )}
      </header>

      <AiRecommendationFeedback
        rating={feedback}
        onChange={handleFeedbackChange}
      />

      {coverage && <AiCoverageSummary coverage={coverage} />}

      <div className="ai-recommendations-step__sections">
        {groupedActive.map((group) => (
          <AiRecommendationSection
            key={group.label}
            items={group.items}
            onRemove={handleRemove}
            onRestore={handleRestore}
          />
        ))}
      </div>

      {removed.length > 0 && (
        <Accordion
          className="ai-recommendations-step__removed"
          isOpen={removedExpanded}
          setIsExpanded={setRemovedExpanded}
        >
          <AccordionHeader>
            <AccordionHeaderName>
              <span className="ai-recommendations-step__removed-label">
                Removed ({removed.length})
              </span>
            </AccordionHeaderName>
          </AccordionHeader>
          <AccordionBody>
            <div className="ai-recommendations-step__removed-items">
              <AiRecommendationSection
                label="Removed items"
                items={removed}
                onRemove={handleRemove}
                onRestore={handleRestore}
              />
            </div>
          </AccordionBody>
        </Accordion>
      )}
    </div>
  );
};
